import { Button } from "@mantine/core";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  AREAS_OF_EXPERTISE_OPTIONS,
  TRAINING_PHILOSOPHY_OPTIONS,
} from "../../../constants/training";
import type { TransformedTrainer } from "../../../types/portal";
import EmptyState from "../../shared/EmptyState";

interface TrainerFilters {
  search: string;
  philosophy: string[];
  expertise: string[];
  credentials: string[];
}

interface DropdownState {
  philosophy: boolean;
  expertise: boolean;
  credentials: boolean;
}

type FilterType = "philosophy" | "expertise" | "credentials";

const CREDENTIAL_OPTIONS = [
  "CDT",
  "CPDT",
  "CPDT-KA",
  "KPA-CTP",
  "CBCC-KA",
  "CCPDT",
  "IAABC",
  "VSPDT",
  "ABC",
  "Other",
];

const renderStars = (rating: number, size: "small" | "normal" = "normal"): React.ReactNode[] => {
  const stars: React.ReactNode[] = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 !== 0;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
  const sizeClass = size === "small" ? "text-[0.9rem]" : "text-[1.2rem]";

  for (let i = 0; i < fullStars; i++) {
    stars.push(
      <span key={i} className={sizeClass}>
        ⭐
      </span>,
    );
  }
  if (hasHalfStar) {
    stars.push(
      <span key="half" className={sizeClass}>
        ✨
      </span>,
    );
  }
  for (let i = 0; i < emptyStars; i++) {
    stars.push(
      <span key={`empty-${i}`} className={sizeClass}>
        ☆
      </span>,
    );
  }
  return stars;
};

interface FindTrainerTabProps {
  activeTrainers: TransformedTrainer[];
  loading: boolean;
  error: string | null;
  refreshSuccess: boolean;
  onRefresh: () => void;
  onStartTraining: (trainer: TransformedTrainer) => void;
}

const FindTrainerTab: React.FC<FindTrainerTabProps> = ({
  activeTrainers,
  loading,
  error,
  refreshSuccess,
  onRefresh,
  onStartTraining,
}) => {
  const [filters, setFilters] = useState<TrainerFilters>({
    search: "",
    philosophy: [],
    expertise: [],
    credentials: [],
  });
  const [openDropdowns, setOpenDropdowns] = useState<DropdownState>({
    philosophy: false,
    expertise: false,
    credentials: false,
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (!(event.target as HTMLElement).closest("[data-dropdown]")) {
        setOpenDropdowns({ philosophy: false, expertise: false, credentials: false });
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredTrainers = useMemo(() => {
    let filtered = [...activeTrainers];
    if (filters.search) {
      const q = filters.search.toLowerCase();
      filtered = filtered.filter(
        (t) => t.name.toLowerCase().includes(q) || t.bio.toLowerCase().includes(q),
      );
    }
    if (filters.philosophy.length > 0) {
      filtered = filtered.filter(
        (t) => t.philosophy && filters.philosophy.some((p) => t.philosophy.includes(p)),
      );
    }
    if (filters.expertise.length > 0) {
      filtered = filtered.filter(
        (t) => t.specializations && filters.expertise.some((e) => t.specializations.includes(e)),
      );
    }
    if (filters.credentials.length > 0) {
      filtered = filtered.filter(
        (t) => t.credentials && filters.credentials.some((c) => t.credentials.includes(c)),
      );
    }
    return filtered;
  }, [activeTrainers, filters]);

  const handleFilterToggle = (filterType: FilterType, value: string): void => {
    setFilters((prev) => {
      const current = prev[filterType];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [filterType]: next };
    });
  };

  const toggleDropdown = (name: FilterType): void => {
    setOpenDropdowns((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const hasActiveFilters =
    filters.search ||
    filters.philosophy.length > 0 ||
    filters.expertise.length > 0 ||
    filters.credentials.length > 0;

  const resetFilters = (): void =>
    setFilters({ search: "", philosophy: [], expertise: [], credentials: [] });

  const renderDropdown = (
    name: FilterType,
    label: string,
    options: string[],
    widthClass: string,
  ): React.ReactNode => (
    <div className={`relative ${widthClass}`} data-dropdown>
      <button
        onClick={() => toggleDropdown(name)}
        className="py-2 px-3 rounded-md border border-border bg-card text-foreground text-[0.85rem] w-full cursor-pointer text-left flex justify-between items-center box-border"
      >
        <span>
          {label} {filters[name].length > 0 ? `(${filters[name].length})` : ""}
        </span>
        <span>{openDropdowns[name] ? "▲" : "▼"}</span>
      </button>
      {openDropdowns[name] && (
        <div className="absolute top-full left-0 right-0 bg-card border border-border rounded-md mt-1 max-h-[200px] overflow-y-auto z-[1000] shadow-lg">
          {options.map((opt) => (
            <label
              key={opt}
              className="flex items-center py-2 px-3 cursor-pointer border-b border-border/50 text-foreground text-[0.85rem] bg-card hover:bg-muted"
            >
              <input
                type="checkbox"
                checked={filters[name].includes(opt)}
                onChange={() => handleFilterToggle(name, opt)}
                className="mr-2 cursor-pointer"
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-card rounded-xl p-4 sm:p-6 shadow-lg border border-border min-h-[400px]">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
        <h2 className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
          Active Trainers
        </h2>
        <Button
          onClick={onRefresh}
          disabled={loading}
          className={
            refreshSuccess
              ? "bg-success text-white"
              : loading
                ? "bg-border cursor-wait"
                : "bg-muted border-border text-muted-foreground"
          }
          variant="default"
        >
          {loading ? "Refreshing..." : refreshSuccess ? "Refreshed!" : "Refresh"}
        </Button>
      </div>

      {!loading && activeTrainers.length > 0 && (
        <div className="bg-muted rounded-lg px-3 py-2 mb-4 border border-border">
          <div className="flex flex-wrap gap-2 items-start">
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="py-2 px-2.5 rounded-md border border-border bg-input text-foreground text-[0.85rem] min-w-[140px] flex-[0_1_200px]"
              placeholder="Search..."
            />
            {renderDropdown(
              "philosophy",
              "Training Philosophy",
              [...TRAINING_PHILOSOPHY_OPTIONS],
              "flex-[0_1_220px] min-w-[180px]",
            )}
            {renderDropdown(
              "expertise",
              "Area of Expertise",
              [...AREAS_OF_EXPERTISE_OPTIONS],
              "flex-[0_1_220px] min-w-[180px]",
            )}
            {renderDropdown(
              "credentials",
              "Credentials",
              CREDENTIAL_OPTIONS,
              "flex-[0_1_180px] min-w-[150px]",
            )}
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="py-2 px-3 rounded-md border border-border bg-card text-muted-foreground text-[0.85rem] cursor-pointer hover:bg-muted"
              >
                Reset Filters
              </button>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center text-[0.9375rem] text-muted-foreground py-12">
          Loading trainers...
        </div>
      ) : error ? (
        <div className="text-center text-[0.9375rem] text-destructive p-6 bg-destructive/10 border border-destructive/25 rounded-lg">
          {error}
        </div>
      ) : activeTrainers.length === 0 ? (
        <EmptyState description="No active trainers available" />
      ) : filteredTrainers.length === 0 ? (
        <EmptyState description="No trainers found matching your filters" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fill,minmax(380px,1fr))] gap-4 max-h-[600px] overflow-y-auto pr-2">
          {filteredTrainers.map((trainer) => (
            <div
              key={trainer.id}
              className="bg-muted rounded-xl p-5 border border-border shadow-sm transition-all duration-200 relative hover:border-primary/40 hover:shadow-md flex flex-col h-full"
            >
              <div className="flex items-center mb-4 gap-3">
                <div className="w-[60px] h-[60px] rounded-full bg-primary flex items-center justify-center text-primary-foreground text-2xl font-bold shrink-0">
                  {trainer.photo}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xl font-bold text-foreground mb-1">{trainer.name}</div>
                  <div className="text-[0.85rem] text-muted-foreground">
                    {trainer.experience} experience
                  </div>
                </div>
                <div
                  className={`flex items-center gap-1.5 text-[0.8rem] font-semibold py-1 px-2 rounded-xl shrink-0 ${
                    trainer.isOnline
                      ? "bg-success/10 text-success"
                      : "bg-destructive/10 text-destructive"
                  }`}
                >
                  <div
                    className={`w-2 h-2 rounded-full ${trainer.isOnline ? "bg-success" : "bg-destructive"}`}
                  ></div>
                  {trainer.isOnline ? "Online" : "Offline"}
                </div>
              </div>

              <div className="flex items-center gap-2 mb-4">
                {renderStars(trainer.overallRating)}
                <span className="text-base font-semibold text-foreground">
                  {trainer.overallRating}
                </span>
                <span className="text-[0.85rem] text-muted-foreground">
                  ({trainer.totalReviews} reviews)
                </span>
              </div>

              <div className="bg-primary/10 rounded-lg p-3 mb-4">
                <div className="text-[0.9rem] font-semibold text-foreground mb-1">
                  Live Session Pricing
                </div>
                <div className="text-[0.85rem] text-muted-foreground leading-relaxed">
                  <strong>${trainer.baseRate}</strong> first 10 minutes +{" "}
                  <strong>${trainer.perMinuteRate}/min</strong> after
                  <br />
                  <em>Instant connection - start training immediately!</em>
                </div>
              </div>

              <div className="text-[0.9rem] text-foreground leading-relaxed mb-4">
                {trainer.bio}
              </div>

              {trainer.specializations && trainer.specializations.length > 0 && (
                <div className="mb-4">
                  <div className="text-[0.85rem] font-semibold text-foreground mb-2">
                    Specializations:
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {trainer.specializations.map((spec, index) => (
                      <span
                        key={index}
                        className="bg-primary/10 text-primary py-1.5 px-3 rounded-[20px] text-xs border border-primary/20 font-medium"
                      >
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {trainer.philosophy && trainer.philosophy.length > 0 && (
                <div className="mb-4">
                  <div className="text-[0.85rem] font-semibold text-foreground mb-2">
                    Training Philosophy:
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {trainer.philosophy.map((phil, index) => (
                      <span
                        key={index}
                        className="bg-primary/10 text-primary py-1.5 px-3 rounded-[20px] text-xs border border-primary/20 font-medium"
                      >
                        {phil}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-auto pt-4 border-t border-border">
                <Button
                  fullWidth
                  radius="md"
                  className={
                    trainer.isOnline ? "" : "!bg-muted !text-muted-foreground !cursor-not-allowed"
                  }
                  disabled={!trainer.isOnline}
                  onClick={() => trainer.isOnline && onStartTraining(trainer)}
                >
                  {trainer.isOnline ? "Start Training Now" : "Currently Offline"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FindTrainerTab;
